/** E-Commerce Store — dark luxury glamorous storefront aesthetic. */

export const ECOMMERCE_STORE_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500;1,600&family=Montserrat:wght@300;400;500;600;700&display=swap');

html { scroll-behavior: smooth; }
body { font-family: 'Montserrat', system-ui, sans-serif; background: #0A0A0A; }
.font-display { font-family: 'Playfair Display', Georgia, serif; }

@keyframes lux-shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes lux-float {
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50% { transform: translateY(-10px); opacity: 0.85; }
}
@keyframes lux-spotlight {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.75; transform: scale(1.05); }
}

.shimmer-text {
  background: linear-gradient(105deg, #C9A96E 0%, #E8E0D0 20%, #C0C0C0 40%, #B8878C 60%, #F5E6C8 80%, #C9A96E 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: lux-shimmer 6s linear infinite;
}
.dark-glossy {
  background: linear-gradient(155deg, rgba(28,26,30,0.95) 0%, rgba(18,16,20,0.98) 50%, rgba(22,20,24,0.95) 100%);
  box-shadow: 0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,169,110,0.08), inset 0 -1px 0 rgba(0,0,0,0.4);
}
.dark-glossy:hover {
  box-shadow: 0 20px 56px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,169,110,0.2), inset 0 1px 0 rgba(201,169,110,0.12);
}
.btn-luxury {
  background: linear-gradient(135deg, #A67B5B 0%, #C9A96E 35%, #E8E0D0 65%, #B8878C 100%);
  background-size: 200% auto;
  color: #0A0A0A;
  transition: all 0.4s ease;
}
.btn-luxury:hover {
  background-position: right center;
  box-shadow: 0 8px 36px rgba(201,169,110,0.35), 0 0 24px rgba(201,169,110,0.15);
  transform: translateY(-2px);
}
.btn-luxury-outline {
  background: rgba(18,16,20,0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(201,169,110,0.3);
  color: #E8E0D0;
  transition: all 0.35s ease;
}
.btn-luxury-outline:hover {
  border-color: rgba(192,192,192,0.5);
  box-shadow: 0 0 28px rgba(201,169,110,0.12);
  color: #F5F0E8;
}
.lux-nav-link { position: relative; }
.lux-nav-link::after {
  content: '';
  position: absolute;
  bottom: -4px; left: 50%; width: 0; height: 1px;
  background: linear-gradient(90deg, #C9A96E, #C0C0C0);
  transition: all 0.35s ease;
  transform: translateX(-50%);
}
.lux-nav-link:hover::after { width: 100%; }
.lux-nav-link:hover { color: #C9A96E !important; }
.product-shimmer::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 35%, rgba(201,169,110,0.12) 50%, rgba(192,192,192,0.08) 55%, transparent 65%);
  background-size: 200% 100%;
  animation: lux-shimmer 4s ease-in-out infinite;
  pointer-events: none;
}
.spotlight {
  background: radial-gradient(ellipse at center, rgba(201,169,110,0.15) 0%, transparent 70%);
}
`,

  'src/App.js': `import React from 'react';
import './index.css';
import Navbar from './components/Navbar';
import HeroBanner from './components/HeroBanner';
import FeaturedCategories from './components/FeaturedCategories';
import ProductGrid from './components/ProductGrid';
import PromoBanner from './components/PromoBanner';
import Testimonials from './components/Testimonials';
import Newsletter from './components/Newsletter';
import Footer from './components/Footer';

export default function App() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0A0A0A] text-[#E8E0D0] antialiased selection:bg-[#C9A96E]/25 selection:text-[#F5F0E8]">
      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(ellipse_at_15%_0%,rgba(201,169,110,0.08)_0%,transparent_45%),radial-gradient(ellipse_at_85%_10%,rgba(184,135,140,0.06)_0%,transparent_40%),radial-gradient(ellipse_at_50%_100%,rgba(30,26,31,0.8)_0%,#0A0A0A_70%)]" />
      <div className="pointer-events-none fixed inset-0 z-0 bg-[linear-gradient(180deg,transparent_0%,rgba(26,20,16,0.4)_50%,rgba(10,10,10,1)_100%)]" />
      <div className="relative z-10">
        <Navbar />
        <main>
          <HeroBanner />
          <FeaturedCategories />
          <ProductGrid />
          <PromoBanner />
          <Testimonials />
          <Newsletter />
        </main>
        <Footer />
      </div>
    </div>
  );
}
`,

  'src/data/products.js': `export const CATEGORIES = [
  { name: 'New Arrivals', tag: 'Just dropped', accent: 'from-[#1E1A1F] via-[#2A2228] to-[#C9A96E]/20', count: 48 },
  { name: 'Evening Wear', tag: 'Glamour edit', accent: 'from-[#0A0A0A] via-[#1A1410] to-[#B8878C]/25', count: 32 },
  { name: 'Beauty & Glow', tag: 'Radiance', accent: 'from-[#161618] via-[#1E1A1F] to-[#A67B5B]/20', count: 24 },
  { name: 'Accessories', tag: 'Finishing touches', accent: 'from-[#121214] via-[#1A1410] to-[#C0C0C0]/10', count: 18 },
];

export const PRODUCTS = [
  {
    id: 1,
    name: 'Champagne Silk Gown',
    price: 428,
    originalPrice: 568,
    rating: 4.9,
    reviews: 124,
    badge: 'Icon',
    accent: 'from-[#0A0A0A] via-[#1E1A1F] to-[#B8878C]/30',
  },
  {
    id: 2,
    name: 'Rose Gold Clutch',
    price: 198,
    rating: 4.9,
    reviews: 89,
    badge: 'New',
    accent: 'from-[#1A1410] via-[#2A2228] to-[#C9A96E]/25',
  },
  {
    id: 3,
    name: 'Pearl Embroidered Blazer',
    price: 345,
    rating: 5.0,
    reviews: 203,
    accent: 'from-[#161618] via-[#1E1A1F] to-[#C0C0C0]/15',
  },
  {
    id: 4,
    name: 'Lavender Satin Heels',
    price: 286,
    rating: 4.8,
    reviews: 67,
    accent: 'from-[#0A0A0A] via-[#1A1410] to-[#B8878C]/20',
  },
  {
    id: 5,
    name: 'Gold Chain Necklace',
    price: 495,
    rating: 4.9,
    reviews: 156,
    badge: 'Limited',
    accent: 'from-[#1A1410] via-[#C9A96E]/15 to-[#0A0A0A]',
  },
  {
    id: 6,
    name: 'Velvet Evening Wrap',
    price: 242,
    rating: 4.7,
    reviews: 312,
    accent: 'from-[#1E1A1F] via-[#161618] to-[#A67B5B]/15',
  },
  {
    id: 7,
    name: 'Shimmer Body Oil Set',
    price: 78,
    originalPrice: 98,
    rating: 4.9,
    reviews: 94,
    badge: 'Glow',
    accent: 'from-[#2A2228] via-[#1E1A1F] to-[#C9A96E]/20',
  },
  {
    id: 8,
    name: 'Crystal Drop Earrings',
    price: 158,
    rating: 5.0,
    reviews: 178,
    accent: 'from-[#0A0A0A] via-[#161618] to-[#C0C0C0]/12',
  },
];

export const TESTIMONIALS = [
  {
    name: 'Isabella Moreau',
    role: 'Fashion Director, Paris',
    quote: 'Every piece radiates luxury — the packaging alone feels like opening a jewel box. Aurelia is my secret for red-carpet moments.',
    rating: 5,
  },
  {
    name: 'Camille Ashford',
    role: 'Beauty Editor',
    quote: 'The shimmer collection is extraordinary. Glossy textures, elegant silhouettes, and that unmistakable champagne glow.',
    rating: 5,
  },
  {
    name: 'Valentina Reyes',
    role: 'Luxury Lifestyle Creator',
    quote: 'This boutique understands glamour. From the first click to unboxing — pure elegance at every touchpoint.',
    rating: 5,
  },
];
`,

  'src/components/Navbar.js': `import React, { useState } from 'react';

const CATS = ['Collections', 'Beauty', 'Accessories', 'Sale'];

export default function Navbar() {
  const [cartCount] = useState(2);

  return (
    <header className="sticky top-0 z-50 border-b border-[#C9A96E]/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-4 md:px-8">
        <a href="#" className="font-display shrink-0 text-2xl font-semibold tracking-wide text-[#F5F0E8]">
          Aure<span className="shimmer-text italic">lia</span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {CATS.map((c) => (
            <a
              key={c}
              href={'#' + c.toLowerCase()}
              className="lux-nav-link text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9A9290] transition"
            >
              {c}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <div className="hidden items-center gap-2 rounded-full border border-[#C9A96E]/20 bg-[#161618]/80 px-4 py-2 backdrop-blur-sm sm:flex">
            <span className="text-[#C9A96E]">✦</span>
            <input
              type="text"
              placeholder="Search the boutique…"
              className="w-36 bg-transparent text-xs font-medium tracking-wide text-[#E8E0D0] outline-none placeholder:text-[#6A6560] md:w-44"
            />
          </div>

          <button
            type="button"
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#C9A96E]/25 bg-gradient-to-br from-[#1E1A1F] to-[#0A0A0A] text-[#E8E0D0] transition hover:border-[#C9A96E]/50 hover:shadow-[0_0_24px_rgba(201,169,110,0.2)]"
            aria-label="Shopping cart"
          >
            <span className="text-base">🛍</span>
            {cartCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-[#C9A96E] to-[#A67B5B] text-[9px] font-bold text-[#0A0A0A] shadow-[0_2px_10px_rgba(201,169,110,0.5)]">
                {cartCount}
              </span>
            )}
          </button>

          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C9A96E]/25 bg-gradient-to-br from-[#1E1A1F] to-[#0A0A0A] text-[#E8E0D0] transition hover:border-[#C0C0C0]/40 hover:shadow-[0_0_24px_rgba(192,192,192,0.1)]"
            aria-label="Account"
          >
            <span className="text-base">♡</span>
          </button>
        </div>
      </div>
    </header>
  );
}
`,

  'src/components/HeroBanner.js': `import React from 'react';

export default function HeroBanner() {
  return (
    <section className="relative overflow-hidden px-5 pb-20 pt-12 md:px-8 md:pb-28 md:pt-16">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[800px] -translate-x-1/2 animate-[lux-spotlight_8s_ease-in-out_infinite] rounded-full bg-[radial-gradient(ellipse,rgba(201,169,110,0.12)_0%,transparent_65%)]" />
      <div className="pointer-events-none absolute -left-20 top-32 h-64 w-64 animate-[lux-float_7s_ease-in-out_infinite] rounded-full bg-[#B8878C]/8 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-20 h-80 w-80 animate-[lux-float_9s_ease-in-out_infinite] rounded-full bg-[#C9A96E]/6 blur-3xl" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#C9A96E]/25 bg-[#161618]/80 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#C9A96E] shadow-[0_4px_24px_rgba(0,0,0,0.4)] backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#C9A96E] to-[#C0C0C0] shadow-[0_0_10px_rgba(201,169,110,0.7)]" />
            Holiday Glamour Collection
          </p>
          <h1 className="font-display text-[2.75rem] font-semibold leading-[1.06] tracking-tight text-[#F5F0E8] md:text-[3.75rem]">
            Where{' '}
            <span className="shimmer-text italic">elegance</span>
            <br />
            meets the night
          </h1>
          <p className="mt-6 max-w-md text-sm font-light leading-relaxed tracking-wide text-[#9A9290] md:text-base">
            A cinematic luxury edit — champagne silks, rose gold accents, and pearl-finished essentials for the modern icon.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#shop" className="btn-luxury rounded-full px-8 py-3.5 text-[10px] font-bold uppercase tracking-[0.18em]">
              Shop the Edit
            </a>
            <a href="#categories" className="btn-luxury-outline rounded-full px-8 py-3.5 text-[10px] font-bold uppercase tracking-[0.18em]">
              Explore Collections
            </a>
          </div>
          <div className="mt-14 flex flex-wrap gap-10 border-t border-[#C9A96E]/10 pt-8">
            {[['Complimentary', 'Gift wrapping'], ['Express', 'Worldwide shipping'], ['VIP', 'Early access']].map(([t, d]) => (
              <div key={t}>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#C9A96E]">{t}</p>
                <p className="mt-1 text-xs font-light text-[#6A6560]">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#C9A96E]/10 via-transparent to-[#B8878C]/10 blur-xl" />
          <div className="relative overflow-hidden rounded-[2rem] border border-[#C9A96E]/20 bg-gradient-to-br from-[#161618] via-[#1E1A1F] to-[#0A0A0A] p-1 shadow-[0_32px_80px_rgba(0,0,0,0.6)]">
            <div className="relative grid grid-cols-5 gap-1.5 rounded-[1.85rem] bg-[#0A0A0A] p-3">
              <div className="col-span-3 row-span-2 flex aspect-[3/4] items-end overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E1A1F] via-[#B8878C]/20 to-[#0A0A0A] p-6 product-shimmer relative spotlight">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9A96E]/80">Featured</p>
                  <p className="font-display mt-1 text-2xl text-[#F5F0E8]">Evening<br />Collection</p>
                </div>
              </div>
              <div className="col-span-2 flex aspect-square items-center justify-center rounded-2xl bg-gradient-to-br from-[#1A1410] to-[#C9A96E]/20 product-shimmer relative">
                <span className="shimmer-text text-3xl">✦</span>
              </div>
              <div className="col-span-2 flex aspect-square items-center justify-center rounded-2xl bg-gradient-to-br from-[#161618] to-[#C0C0C0]/10 product-shimmer relative">
                <span className="font-display text-sm text-[#C0C0C0]/60">Beauty</span>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 rounded-2xl border border-[#C9A96E]/25 bg-[#161618]/95 px-6 py-3 shadow-[0_16px_48px_rgba(0,0,0,0.5)] backdrop-blur-md">
            <p className="text-center text-[10px] font-bold uppercase tracking-[0.2em] shimmer-text">Exclusive</p>
            <p className="text-center font-display text-sm text-[#F5F0E8]">Up to 40% Off Select Styles</p>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/FeaturedCategories.js': `import React from 'react';
import { CATEGORIES } from '../data/products';

export default function FeaturedCategories() {
  return (
    <section id="categories" className="relative bg-gradient-to-b from-[#0A0A0A] via-[#121214] to-[#0A0A0A] px-5 py-20 md:px-8 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C9A96E]/30 to-transparent" />
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#B8878C]">Curated collections</p>
          <h2 className="font-display mt-4 text-4xl font-semibold text-[#F5F0E8] md:text-5xl">
            Shop by <span className="shimmer-text italic">glamour</span>
          </h2>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((cat) => (
            <a
              key={cat.name}
              href={'#' + cat.name.toLowerCase().replace(/ /g, '-')}
              className="group relative overflow-hidden rounded-2xl border border-[#C9A96E]/10 transition duration-500 hover:-translate-y-2 hover:border-[#C9A96E]/30 hover:shadow-[0_24px_64px_rgba(0,0,0,0.5),0_0_40px_rgba(201,169,110,0.08)]"
            >
              <div className={'relative flex h-56 flex-col justify-end bg-gradient-to-br p-6 ' + cat.accent}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/30 to-transparent" />
                <div className="product-shimmer absolute inset-0 opacity-0 transition group-hover:opacity-100" />
                <div className="relative">
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#C9A96E]/80">{cat.tag}</p>
                  <h3 className="font-display mt-1 text-2xl font-semibold text-[#F5F0E8] transition group-hover:tracking-wide">
                    {cat.name}
                  </h3>
                  <p className="mt-2 text-xs font-light text-[#9A9290]">{cat.count} pieces</p>
                  <span className="mt-4 inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-[#C9A96E] opacity-0 transition group-hover:opacity-100">
                    Discover →
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/ProductGrid.js': `import React, { useState } from 'react';
import { PRODUCTS } from '../data/products';

function ProductCard({ product }) {
  const [wishlisted, setWishlisted] = useState(false);

  return (
    <article className="dark-glossy group flex flex-col overflow-hidden rounded-2xl border border-[#C9A96E]/10 transition duration-500 hover:-translate-y-2">
      <div className={'product-shimmer relative flex aspect-[4/5] items-center justify-center overflow-hidden bg-gradient-to-br ' + product.accent}>
        {product.badge && (
          <span className="absolute left-4 top-4 rounded-full border border-[#C9A96E]/30 bg-[#0A0A0A]/80 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.15em] shimmer-text backdrop-blur-sm">
            {product.badge}
          </span>
        )}
        <button
          type="button"
          onClick={() => setWishlisted(!wishlisted)}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-[#C9A96E]/20 bg-[#161618]/90 text-sm text-[#E8E0D0] backdrop-blur-sm transition hover:scale-110 hover:border-[#C9A96E]/50 hover:shadow-[0_0_20px_rgba(201,169,110,0.2)]"
          aria-label="Add to wishlist"
        >
          {wishlisted ? '♥' : '♡'}
        </button>
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6A6560]">Product image</span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-1 text-[10px] tracking-wider text-[#C9A96E]">
          {'★'.repeat(Math.floor(product.rating))}
          <span className="ml-1 font-light text-[#6A6560]">({product.reviews})</span>
        </div>
        <h3 className="font-display mt-2 text-lg font-semibold text-[#F5F0E8]">{product.name}</h3>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-lg font-semibold shimmer-text">\${product.price}</span>
          {product.originalPrice && (
            <span className="text-sm font-light text-[#6A6560] line-through">\${product.originalPrice}</span>
          )}
        </div>
        <button
          type="button"
          className="btn-luxury mt-4 w-full rounded-full py-2.5 text-[10px] font-bold uppercase tracking-[0.15em]"
        >
          Add to bag
        </button>
      </div>
    </article>
  );
}

export default function ProductGrid() {
  return (
    <section id="shop" className="relative bg-[#0A0A0A] px-5 py-20 md:px-8 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/20 to-transparent" />
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#B8878C]">The boutique edit</p>
            <h2 className="font-display mt-4 text-4xl font-semibold text-[#F5F0E8] md:text-5xl">
              Signature <span className="shimmer-text italic">pieces</span>
            </h2>
          </div>
          <a href="#all" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#C9A96E] transition hover:text-[#E8E0D0]">
            View all →
          </a>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/PromoBanner.js': `import React from 'react';

export default function PromoBanner() {
  return (
    <section className="px-5 py-10 md:px-8">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] border border-[#C9A96E]/20 shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#1A1410] to-[#1E1A1F]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_25%_50%,rgba(201,169,110,0.18)_0%,transparent_55%),radial-gradient(ellipse_at_75%_50%,rgba(184,135,140,0.1)_0%,transparent_50%)]" />
        <div className="product-shimmer pointer-events-none absolute inset-0 opacity-40" />
        <div className="relative flex flex-col items-center gap-8 px-8 py-16 text-center md:flex-row md:justify-between md:px-16 md:py-20 md:text-left">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C9A96E]">Members only</p>
            <h2 className="font-display mt-4 text-3xl font-semibold text-[#F5F0E8] md:text-4xl">
              The <span className="shimmer-text italic">Radiance</span> Event
            </h2>
            <p className="mt-4 max-w-md text-sm font-light leading-relaxed tracking-wide text-[#9A9290]">
              Unlock early access to our cinematic holiday capsule — champagne silks, crystal accessories, and glow essentials.
            </p>
          </div>
          <a
            href="#sale"
            className="btn-luxury shrink-0 rounded-full px-10 py-4 text-[10px] font-bold uppercase tracking-[0.18em]"
          >
            Shop the Event
          </a>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Testimonials.js': `import React from 'react';
import { TESTIMONIALS } from '../data/products';

export default function Testimonials() {
  return (
    <section className="relative bg-gradient-to-b from-[#0A0A0A] via-[#121214] to-[#0A0A0A] px-5 py-20 md:px-8 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#B8878C]/30 to-transparent" />
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#B8878C]">Client stories</p>
          <h2 className="font-display mt-4 text-4xl font-semibold text-[#F5F0E8] md:text-5xl">
            Loved by <span className="shimmer-text italic">icons</span>
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <blockquote
              key={t.name}
              className="dark-glossy flex flex-col rounded-2xl border border-[#C9A96E]/10 p-8 transition duration-500 hover:border-[#C9A96E]/25 hover:shadow-[0_20px_56px_rgba(0,0,0,0.4)]"
            >
              <div className="mb-4 flex gap-0.5 text-sm text-[#C9A96E]">{'★'.repeat(t.rating)}</div>
              <p className="flex-1 font-display text-base italic leading-relaxed text-[#9A9290]">&ldquo;{t.quote}&rdquo;</p>
              <footer className="mt-8 border-t border-[#C9A96E]/10 pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#F5F0E8]">{t.name}</p>
                <p className="mt-1 text-[10px] font-light uppercase tracking-[0.1em] text-[#B8878C]">{t.role}</p>
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Newsletter.js': `import React, { useState } from 'react';

export default function Newsletter() {
  const [email, setEmail] = useState('');

  return (
    <section className="px-5 py-20 md:px-8 md:py-28">
      <div className="relative mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-[#C9A96E]/20 bg-gradient-to-br from-[#161618] via-[#1E1A1F] to-[#0A0A0A] px-8 py-14 text-center shadow-[0_24px_64px_rgba(0,0,0,0.5)] md:px-14 md:py-16">
        <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#C9A96E]/8 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-[#B8878C]/6 blur-3xl" />
        <div className="product-shimmer pointer-events-none absolute inset-0 opacity-20" />
        <div className="relative">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#B8878C]">The inner circle</p>
          <h2 className="font-display mt-4 text-3xl font-semibold text-[#F5F0E8] md:text-4xl">
            Join the <span className="shimmer-text italic">Aurelia</span> list
          </h2>
          <p className="mt-4 text-sm font-light leading-relaxed tracking-wide text-[#9A9290]">
            Exclusive drops, VIP previews, and champagne-worthy offers — delivered with glamour.
          </p>
          <form
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="w-full rounded-full border border-[#C9A96E]/20 bg-[#0A0A0A]/80 px-6 py-3.5 text-sm font-light text-[#E8E0D0] outline-none backdrop-blur-sm transition focus:border-[#C9A96E]/40 focus:shadow-[0_0_24px_rgba(201,169,110,0.12)] sm:max-w-xs"
            />
            <button
              type="submit"
              className="btn-luxury rounded-full px-8 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em]"
            >
              Subscribe
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Footer.js': `import React from 'react';

const COLS = [
  { title: 'Boutique', links: ['New Arrivals', 'Evening Wear', 'Beauty', 'Sale'] },
  { title: 'Services', links: ['Personal Styling', 'Gift Concierge', 'Size Guide', 'Contact'] },
  { title: 'House of Aurelia', links: ['Our Story', 'Sustainability', 'Careers', 'Press'] },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-[#C9A96E]/10 bg-[#0A0A0A] px-5 py-16 md:px-8 md:py-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C9A96E]/40 to-transparent" />
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 md:grid-cols-5">
          <div className="md:col-span-2">
            <a href="#" className="font-display text-3xl font-semibold text-[#F5F0E8]">
              Aure<span className="shimmer-text italic">lia</span>
            </a>
            <p className="mt-5 max-w-xs text-sm font-light leading-relaxed tracking-wide text-[#6A6560]">
              A dark luxury destination for fashion-forward icons. Champagne elegance, rose gold radiance, cinematic glamour.
            </p>
            <div className="mt-6 flex gap-3">
              {['✦', '♡', '◇'].map((icon, i) => (
                <span
                  key={i}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C9A96E]/20 bg-[#161618] text-xs text-[#C9A96E] transition hover:border-[#C9A96E]/50 hover:shadow-[0_0_20px_rgba(201,169,110,0.15)]"
                >
                  {icon}
                </span>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9A96E]">{col.title}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((link) => (
                  <li key={link}>
                    <a href={'#' + link.toLowerCase().replace(/\\s/g, '-')} className="text-xs font-light tracking-wide text-[#6A6560] transition hover:text-[#E8E0D0]">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-[#C9A96E]/10 pt-8 sm:flex-row">
          <p className="text-[10px] font-light tracking-[0.15em] text-[#6A6560]">© 2026 Aurelia. Crafted with radiance.</p>
          <div className="flex gap-8">
            {['Instagram', 'Pinterest', 'TikTok'].map((s) => (
              <a key={s} href={'#' + s.toLowerCase()} className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#C9A96E]/60 transition hover:text-[#F5F0E8]">
                {s}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
`,
};
